import { extendTheme, ThemeConfig } from "@chakra-ui/react";
import { mode } from "@chakra-ui/theme-tools";

const config: ThemeConfig = {
  initialColorMode: "light",
  useSystemColorMode: false,
};

export const theme = extendTheme({
  config,
  fonts: {
    heading: "'iransans', sans-serif",
    body: "'iransans', sans-serif",
  },
  colors: {
    brand: {
      50: "#fef2f2",
      100: "#fdd8d7",
      200: "#f9a8a6",
      300: "#f47975",
      400: "#e43f39",
      500: "#ad0b06",
      600: "#8a0905",
      700: "#6a0704",
      800: "#4a0503",
      900: "#2a0301",
    },
    secondary: {
      50: "#fffcf0",
      100: "#fff5d1",
      200: "#ffeca3",
      300: "#ffe375",
      400: "#ffd647",
      500: "#ffc200",
      600: "#cc9b00",
      700: "#997400",
      800: "#664d00",
      900: "#332700",
    },
    neutral: {
      50: "#f7f7f7",
      100: "#e4e4e3",
      200: "#c9c9c7",
      300: "#aeadac",
      400: "#939291",
      500: "#6b6a69",
      600: "#565554",
      700: "#414040",
      800: "#2b2b2b",
      900: "#161616",
    },
    accent1: {
      50: "#e8ecf5",
      100: "#c2cee3",
      200: "#9bafd0",
      300: "#7490bd",
      400: "#4d71aa",
      500: "#112b63",
      600: "#0e2250",
      700: "#0b1a3d",
      800: "#08112a",
      900: "#050917",
    },
    accent2: {
      50: "#e6f7f7",
      100: "#b8e8e8",
      200: "#8ad9d9",
      300: "#5ccaca",
      400: "#2ebbbb",
      500: "#07aaaa",
      600: "#068888",
      700: "#056666",
      800: "#044444",
      900: "#022222",
    },
    light: {
      // Gradient between #ffffff (lightest) and #edf2f8 (darkest)
      50: "#ffffff",
      100: "#fdfefe",
      200: "#fbfcfd",
      300: "#f9fbfd",
      400: "#f7f9fc",
      500: "#f5f8fb",
      600: "#f3f6fa",
      700: "#f1f5fa",
      800: "#eff3f9",
      900: "#edf2f8",
    },
    dark: {
      // Generated gradient between #17243d (lightest) and #0f1c31 (darkest)
      50: "#17243d",
      100: "#16233c",
      200: "#15223a",
      300: "#142139",
      400: "#132038",
      500: "#132036",
      600: "#121f35",
      700: "#111e34",
      800: "#101d32",
      900: "#0f1c31",
    },
    icon: { bg: "#3c3a4e", svg: "#f8a8a6" },
    badge: {
      dark: { bg: "rgba(249, 168, 166, 0.16)", color: "brand.200" },
      light: { bg: "brand.100", color: "brand.800" },
    },
  },
  components: {
    Button: {
      variants: {
        primary: (props: any) => ({
          bg: "brand.500",
          color: "white",
          _hover: {
            _disabled: {
              bg: "brand.500",
              color: "white",
              cursor: "not-allowed",
            },
            bg: "brand.400",
            color: "white",
            transform: "translateY(-2px)",
          },
          _active: {
            bg: "brand.700",
            color: "white",
          },
          _focusVisible: {
            boxShadow: mode(
              "0 0 0 3px rgba(173, 11, 6, 0.25)",
              "0 0 0 3px rgba(173, 11, 6, 0.35)",
            )(props),
          },
        }),
        secondary: (props: any) => ({
          bg: mode("brand.100", "brand.200")(props),
          color: mode("brand.500", "brand.500")(props),
          _hover: {
            bg: mode("brand.200", "brand.300")(props),
            color: mode("brand.500", "white")(props),
            borderColor: mode("brand.500", "white")(props),
            transform: "translateY(-2px)",
          },
          _active: {
            bg: mode("brand.100", "whiteAlpha.200")(props),
            color: mode("brand.500", "white")(props),
            borderColor: mode("brand.500", "white")(props),
          },
          _focusVisible: {
            boxShadow: mode(
              "0 0 0 3px rgba(173, 11, 6, 0.16)",
              "0 0 0 3px rgba(255, 255, 255, 0.2)",
            )(props),
          },
        }),
      },
      defaultProps: {
        colorScheme: "brand",
        variant: "primary",
      },
    },
  },
  styles: {
    global: (props: any) => ({
      body: {
        bg: props.colorMode === "dark" ? "dark.50" : "light.50",
      },
    }),
  },
});
